import { useRef, useState } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useGSAP } from '@gsap/react'
import {
  ArrowDown,
  ArrowUpRight,
  Cloud,
  Code2,
  Layers,
  Linkedin,
  Mail,
  MapPin,
  Menu,
  Network,
  Phone,
  Receipt,
  Server,
  ShoppingBag,
  UtensilsCrossed,
  X,
} from 'lucide-react'

gsap.registerPlugin(ScrollTrigger)

const contactInfo = {
  email: 'jesusesteban12321@gmail.com',
  phones: [
    { label: '+58 412 821 0569', href: 'tel:+584128210569' },
    { label: '+34 670 45 68 43', href: 'tel:+34670456843' },
  ],
  whatsappHref: 'https://wa.me/584128210569',
  location: 'Guárico, San Juan de los Morros, Venezuela',
  linkedin:
    'https://www.linkedin.com/in/jes%C3%BAs-villalta-gonzalez-83368722b/',
}

const saasProducts = [
  {
    name: 'Pymees',
    url: 'https://pymees.store/',
    tag: 'E-Commerce / SaaS',
    description:
      'Plataforma de comercio electrónico y tiendas online optimizada para PYMES.',
    logo: '/images/pymees-iso.png?v=2',
    icon: ShoppingBag,
    glow: 'rgba(0,114,255,0.18)',
  },
  {
    name: 'Pídelo Chévere',
    url: 'http://pidelo-chevere.food/',
    tag: 'Gastronomía / POS',
    description:
      'Sistema integral de gestión de comensales, pedidos y menús digitales.',
    logo: '/images/pidelo-chevere-iso.png?v=2',
    icon: UtensilsCrossed,
    glow: 'rgba(15,159,138,0.18)',
  },
  {
    name: 'Nexus Invoices',
    url: 'https://invoices.nexusdevtech.com/',
    tag: 'Fintech / Facturación',
    description:
      'Facturación, control financiero y generación de comprobantes digitales.',
    logo: '/images/nexus-invoices-iso.png?v=1',
    icon: Receipt,
    glow: 'rgba(0,114,255,0.14)',
  },
  {
    name: 'Protegi2 Cloud',
    url: 'https://www.protegi2.cloud/',
    tag: 'Cloud / Seguridad',
    description:
      'Seguridad, respaldo en la nube y gestión remota de activos críticos.',
    logo: '/images/protegi2-iso.png?v=2',
    icon: Cloud,
    glow: 'rgba(15,159,138,0.16)',
  },
]

const services = [
  {
    title: 'Desarrollo Web & Móvil',
    description: 'Productos rápidos, estables y listos para producción.',
    icon: Code2,
  },
  {
    title: 'Arquitectura Multi-Tenant',
    description: 'SaaS escalable con aislamiento, billing y observabilidad.',
    icon: Layers,
  },
  {
    title: 'Integraciones & APIs',
    description: 'Pagos, facturación electrónica y sistemas críticos.',
    icon: Network,
  },
  {
    title: 'DevOps & Cloud',
    description: 'CI/CD, infraestructura y despliegues seguros.',
    icon: Server,
  },
]

const navLinks = [
  { label: 'Ecosistema', id: 'ecosistema' },
  { label: 'Servicios', id: 'servicios' },
  { label: 'Contacto', id: 'contacto' },
]

function scrollToId(id) {
  const el = document.getElementById(id)
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function BrandMark({ size = 'md' }) {
  const main = size === 'lg' ? 'text-2xl sm:text-3xl' : 'text-sm sm:text-base'
  const domain = size === 'lg' ? 'text-sm' : 'text-[11px]'
  return (
    <span className={`font-display font-bold tracking-tight ${main}`}>
      <span className="text-nexus-ink">nexus</span>
      <span className="bg-gradient-to-r from-[#0072FF] to-[#0F9F8A] bg-clip-text text-transparent">
        dev
      </span>
      <span className="text-nexus-ink">tech</span>
      <span className={`ml-0.5 font-sans font-medium text-nexus-muted ${domain}`}>
        .com
      </span>
    </span>
  )
}

function getLogoFlight(logoEl, targetEl) {
  if (!logoEl || !targetEl) return { x: 0, y: 0, scale: 1 }

  const prevX = gsap.getProperty(logoEl, 'x')
  const prevY = gsap.getProperty(logoEl, 'y')
  const prevScale = gsap.getProperty(logoEl, 'scale')
  const prevZ = gsap.getProperty(logoEl, 'z')

  // Medir en posición natural del header (sin transform)
  gsap.set(logoEl, { x: 0, y: 0, scale: 1, z: 0 })
  const from = logoEl.getBoundingClientRect()
  const to = targetEl.getBoundingClientRect()

  const fromCx = from.left + from.width / 2
  const fromCy = from.top + from.height / 2
  const toCx = to.left + to.width / 2
  const toCy = to.top + to.height / 2

  const scaleH = to.height / Math.max(from.height, 1)
  const maxW = Math.min(
    to.width || window.innerWidth * 0.86,
    window.innerWidth * 0.88,
  )
  const scaleW = maxW / Math.max(from.width, 1)
  const scale = Math.min(Math.max(scaleH, 2.1), scaleW, 2.75)

  // Restaurar transform previa (evita saltos en refresh)
  gsap.set(logoEl, { x: prevX, y: prevY, scale: prevScale, z: prevZ })

  return {
    x: toCx - fromCx,
    y: toCy - fromCy,
    scale,
  }
}

export default function FoldingScrollLanding() {
  const rootRef = useRef(null)
  const sceneRef = useRef(null)
  const headerChromeRef = useRef(null)
  const logoRef = useRef(null)
  const logoTargetRef = useRef(null)
  const heroRef = useRef(null)
  const stageRef = useRef(null)
  const footerRef = useRef(null)
  const [menuOpen, setMenuOpen] = useState(false)

  useGSAP(
    () => {
      const mm = gsap.matchMedia()

      mm.add('(prefers-reduced-motion: reduce)', () => {
        gsap.set(
          [headerChromeRef.current, footerRef.current, stageRef.current, logoRef.current],
          {
            clearProps: 'all',
            opacity: 1,
            scale: 1,
            rotateX: 0,
            x: 0,
            y: 0,
          },
        )
        gsap.set(heroRef.current, {
          clearProps: 'all',
          opacity: 1,
          scale: 1,
          rotateX: 0,
        })
      })

      mm.add('(prefers-reduced-motion: no-preference)', () => {
        const logo = logoRef.current
        const target = logoTargetRef.current

        gsap.set(headerChromeRef.current, {
          opacity: 0.18,
          y: -8,
        })
        gsap.set(footerRef.current, {
          scale: 0.78,
          opacity: 0.28,
          transformOrigin: '50% 100%',
        })
        gsap.set(heroRef.current, {
          scale: 1,
          opacity: 1,
          rotateX: 0,
          transformOrigin: '50% 55%',
        })
        gsap.set(stageRef.current, {
          opacity: 0,
          y: 64,
          scale: 0.9,
          rotateX: 12,
        })

        // Como wearebrand: el logo vive en el header, pero arranca
        // centrado/grande en el hero y “vuela” de vuelta al nav.
        const flight = { x: 0, y: 0, scale: 1 }
        const measureFlight = () => {
          Object.assign(flight, getLogoFlight(logo, target))
        }

        measureFlight()
        gsap.set(logo, {
          x: flight.x,
          y: flight.y,
          scale: flight.scale,
          z: 40,
          transformOrigin: '50% 50%',
          force3D: true,
        })

        const tl = gsap.timeline({
          defaults: { ease: 'none' },
          scrollTrigger: {
            trigger: sceneRef.current,
            start: 'top top',
            end: '+=260%',
            pin: true,
            scrub: 0.65,
            anticipatePin: 1,
            invalidateOnRefresh: true,
            onRefreshInit: () => {
              gsap.set(logo, { x: 0, y: 0, scale: 1, z: 0 })
              measureFlight()
            },
          },
        })

        tl.fromTo(
          logo,
          {
            x: () => flight.x,
            y: () => flight.y,
            scale: () => flight.scale,
            z: 40,
          },
          {
            x: 0,
            y: 0,
            scale: 1,
            z: 10,
            duration: 1,
            ease: 'power1.out',
          },
          0,
        )
          .to(
            heroRef.current,
            {
              scale: 0.48,
              rotateX: 48,
              opacity: 0,
              z: -220,
              filter: 'blur(8px)',
              duration: 1,
            },
            0,
          )
          .to(headerChromeRef.current, { opacity: 1, y: 0, duration: 1 }, 0)
          .to(footerRef.current, { scale: 1, opacity: 1, duration: 1 }, 0)
          .to(
            stageRef.current,
            {
              opacity: 1,
              y: 0,
              scale: 1,
              rotateX: 0,
              duration: 0.65,
            },
            0.28,
          )
      })

      return () => mm.revert()
    },
    { scope: rootRef },
  )

  return (
    <div
      ref={rootRef}
      className="font-sans bg-nexus-mist text-nexus-ink selection:bg-[#0072FF]/20"
    >
      {/* Atmosphere — clara, corporativa */}
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-[#F4F7FB]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(0,114,255,0.10),_transparent_55%),radial-gradient(ellipse_at_bottom_right,_rgba(15,159,138,0.08),_transparent_50%)]" />
        <div className="absolute -left-20 top-[-8%] h-[50vh] w-[50vh] rounded-full bg-[#0072FF]/10 blur-[100px]" />
        <div className="absolute -right-16 bottom-[-8%] h-[45vh] w-[45vh] rounded-full bg-[#0F9F8A]/10 blur-[100px]" />
        <div
          className="absolute inset-0 opacity-[0.45]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgba(11,31,58,0.08) 1px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />
      </div>

      {/* Header fijo (como wearebrand): el logo vuela desde el hero hasta aquí */}
      <header className="fixed inset-x-0 top-0 z-50 border-b border-nexus-line/70 bg-[#F4F7FB]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <a
            ref={logoRef}
            href="#inicio"
            className={`relative flex items-center gap-3 will-change-transform ${menuOpen ? 'invisible md:visible' : ''}`}
            style={{ transformStyle: 'preserve-3d', zIndex: menuOpen ? 1 : 50 }}
            onClick={(e) => {
              e.preventDefault()
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }}
          >
            <img
              src="/images/isologo-nexus.png?v=2"
              alt="NexusDevTech"
              className="h-9 w-9 rounded-full object-cover ring-1 ring-nexus-line sm:h-10 sm:w-10"
            />
            <BrandMark />
          </a>

          <div
            ref={headerChromeRef}
            className="flex items-center will-change-transform"
            style={{ transformStyle: 'preserve-3d' }}
          >
            <nav className="hidden items-center gap-8 md:flex">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  type="button"
                  onClick={() => scrollToId(link.id)}
                  className="text-[13px] font-medium tracking-wide text-nexus-muted transition hover:text-nexus-ink"
                >
                  {link.label}
                </button>
              ))}
              <a
                href={contactInfo.whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full bg-[#0072FF] px-5 py-2.5 text-[13px] font-semibold text-white shadow-soft transition hover:bg-[#0060d6]"
              >
                Cotizar Proyecto
              </a>
            </nav>

            <button
              type="button"
              className="inline-flex rounded-full border border-nexus-line bg-white p-2.5 text-nexus-ink md:hidden"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'}
            >
              {menuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="fixed inset-0 z-[100] flex flex-col bg-[#F4F7FB] md:hidden">
            <div className="flex items-center justify-between border-b border-nexus-line px-4 py-4">
              <div className="flex items-center gap-3">
                <img
                  src="/images/isologo-nexus.png?v=2"
                  alt=""
                  className="h-9 w-9 rounded-full object-cover ring-1 ring-nexus-line"
                />
                <BrandMark />
              </div>
              <button
                type="button"
                className="inline-flex rounded-full border border-nexus-line bg-white p-2.5 text-nexus-ink"
                onClick={() => setMenuOpen(false)}
                aria-label="Cerrar menú"
              >
                <X size={18} />
              </button>
            </div>

            <nav className="flex flex-1 flex-col gap-1 px-4 py-6">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  type="button"
                  onClick={() => {
                    setMenuOpen(false)
                    scrollToId(link.id)
                  }}
                  className="rounded-2xl px-4 py-3.5 text-left text-base font-medium text-nexus-ink transition hover:bg-white"
                >
                  {link.label}
                </button>
              ))}
              <a
                href={contactInfo.whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMenuOpen(false)}
                className="mt-4 inline-flex items-center justify-center rounded-full bg-[#0072FF] px-5 py-3.5 text-sm font-semibold text-white shadow-soft"
              >
                Cotizar Proyecto
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* Fold scene */}
      <section ref={sceneRef} className="relative h-screen overflow-hidden">
        <div
          className="relative mx-auto flex h-full max-w-6xl flex-col px-4 pb-5 pt-20 sm:px-6 lg:px-8"
          style={{ perspective: '1100px', transformStyle: 'preserve-3d' }}
        >
          {/* Center stage */}
          <div className="relative z-10 flex min-h-0 flex-1 items-center justify-center">
            {/* Hero fold panel */}
            <div
              ref={heroRef}
              id="inicio"
              className="absolute inset-x-0 flex items-center justify-center will-change-transform"
              style={{
                transformStyle: 'preserve-3d',
                backfaceVisibility: 'hidden',
              }}
            >
              <div className="relative w-full max-w-3xl px-1 text-center">
                {/* Ancla invisible: el logo del header vuela desde aquí */}
                <div
                  ref={logoTargetRef}
                  aria-hidden
                  className="pointer-events-none mx-auto mb-6 h-28 w-[min(100%,22rem)] sm:mb-8 sm:h-32"
                />

                <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.35em] text-[#0F9F8A]">
                  Software Factory & Studio Hub
                </p>

                <h1 className="font-display text-[2.1rem] font-extrabold leading-[1.05] tracking-tight text-nexus-ink sm:text-5xl lg:text-[3.4rem]">
                  Ecosistema Digital de
                  <br />
                  <span className="bg-gradient-to-r from-[#0072FF] to-[#0F9F8A] bg-clip-text text-transparent">
                    Soluciones de Software
                  </span>
                </h1>

                <p className="mx-auto mt-5 max-w-xl text-[15px] leading-relaxed text-nexus-muted sm:text-base">
                  Plataformas SaaS listas para operar e ingeniería a medida
                  para impulsar operaciones digitales sólidas.
                </p>

                <div className="mt-10 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-nexus-muted">
                  <ArrowDown size={14} className="animate-bounce text-[#0072FF]" />
                  Scroll para desplegar
                </div>
              </div>
            </div>

            {/* Revealed ecosystem stage */}
            <div
              ref={stageRef}
              className="relative z-20 w-full will-change-transform"
              style={{ transformStyle: 'preserve-3d' }}
            >
              <div className="mb-5 flex items-end justify-between gap-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#0072FF]">
                    Productos
                  </p>
                  <h2 className="font-display mt-1 text-xl font-bold text-nexus-ink sm:text-2xl">
                    Ecosistema SaaS
                  </h2>
                </div>
                <p className="hidden max-w-xs text-right text-xs leading-relaxed text-nexus-muted sm:block">
                  Cuatro plataformas en producción. Abre cualquiera para
                  visitarla.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
                {saasProducts.map((product) => {
                  const Icon = product.icon
                  return (
                    <a
                      key={product.name}
                      href={product.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative overflow-hidden rounded-2xl border border-nexus-line bg-white p-4 shadow-soft transition duration-300 hover:-translate-y-1 hover:border-[#0072FF]/35 sm:p-5"
                    >
                      <div className="relative">
                        {product.logo ? (
                          <img
                            src={product.logo}
                            alt=""
                            className="mb-3 h-11 w-11 object-contain"
                          />
                        ) : (
                          <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-[#0072FF] text-white">
                            <Icon size={18} />
                          </div>
                        )}
                        <h3 className="font-display text-sm font-bold text-nexus-ink sm:text-base">
                          {product.name}
                        </h3>
                        <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-nexus-muted">
                          {product.tag}
                        </p>
                        <p className="mt-2 hidden text-xs leading-relaxed text-nexus-muted sm:line-clamp-2 sm:block">
                          {product.description}
                        </p>
                        <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#0072FF]">
                          Abrir
                          <ArrowUpRight
                            size={13}
                            className="transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                          />
                        </span>
                      </div>
                    </a>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Footer edge of fold */}
          <footer
            ref={footerRef}
            className="relative z-40 will-change-transform"
            style={{ transformStyle: 'preserve-3d' }}
          >
            <div className="flex items-center justify-between border-t border-nexus-line pt-4 text-xs text-nexus-muted sm:text-sm">
              <span className="tracking-[0.2em] text-nexus-muted">
                CONNECT · BUILD · INNOVATE
              </span>
              <span className="hidden sm:inline">
                © {new Date().getFullYear()} NexusDevTech
              </span>
              <button
                type="button"
                onClick={() => scrollToId('ecosistema')}
                className="font-medium text-[#0072FF] transition hover:text-[#0060d6]"
              >
                Ver productos ↓
              </button>
            </div>
          </footer>
        </div>
      </section>

      {/* Full ecosystem */}
      <section
        id="ecosistema"
        className="relative scroll-mt-24 px-4 py-24 sm:px-6 lg:px-8 lg:py-32"
      >
        <div className="mx-auto max-w-6xl">
          <div className="max-w-2xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#0072FF]">
              Ecosistema
            </p>
            <h2 className="font-display mt-3 text-3xl font-extrabold tracking-tight text-nexus-ink sm:text-4xl">
              Plataformas listas para
              <span className="bg-gradient-to-r from-[#0072FF] to-[#0F9F8A] bg-clip-text text-transparent">
                {' '}
                operar hoy
              </span>
            </h2>
            <p className="mt-4 text-base leading-relaxed text-nexus-muted">
              Cada producto nace de ingeniería real en producción: e-commerce,
              gastronomía, facturación y seguridad cloud.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2">
            {saasProducts.map((product) => {
              const Icon = product.icon
              return (
                <a
                  key={`full-${product.name}`}
                  href={product.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative overflow-hidden rounded-[1.75rem] border border-nexus-line bg-white p-6 shadow-soft transition duration-300 hover:-translate-y-0.5 hover:border-[#0072FF]/35 sm:p-8"
                >
                  <div className="relative flex items-start justify-between gap-4">
                    <div className="flex items-center gap-4">
                      {product.logo ? (
                        <img
                          src={product.logo}
                          alt=""
                          className="h-14 w-14 object-contain"
                        />
                      ) : (
                        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0072FF] text-white shadow-soft">
                          <Icon size={24} />
                        </div>
                      )}
                      <div>
                        <h3 className="font-display text-xl font-bold text-nexus-ink">
                          {product.name}
                        </h3>
                        <p className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-nexus-muted">
                          {product.tag}
                        </p>
                      </div>
                    </div>
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-nexus-line text-nexus-muted transition group-hover:border-[#0072FF]/40 group-hover:text-[#0072FF]">
                      <ArrowUpRight size={18} />
                    </span>
                  </div>
                  <p className="relative mt-5 max-w-md text-sm leading-relaxed text-nexus-muted">
                    {product.description}
                  </p>
                  <span className="relative mt-6 inline-flex text-sm font-semibold text-[#0072FF]">
                    Abrir plataforma →
                  </span>
                </a>
              )
            })}
          </div>
        </div>
      </section>

      {/* Services */}
      <section
        id="servicios"
        className="relative scroll-mt-24 border-y border-nexus-line bg-white/60 px-4 py-24 sm:px-6 lg:px-8 lg:py-28"
      >
        <div className="mx-auto max-w-6xl">
          <div className="max-w-2xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#0F9F8A]">
              Ingeniería
            </p>
            <h2 className="font-display mt-3 text-3xl font-extrabold tracking-tight text-nexus-ink sm:text-4xl">
              Servicios a medida
            </h2>
            <p className="mt-4 text-base text-nexus-muted">
              Capacidad senior para diseñar, construir y operar software que
              escala con tu negocio.
            </p>
          </div>

          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {services.map((service) => {
              const Icon = service.icon
              return (
                <article
                  key={service.title}
                  className="rounded-3xl border border-nexus-line bg-white p-6 shadow-soft transition hover:border-[#0072FF]/30 sm:p-7"
                >
                  <div className="mb-5 inline-flex rounded-2xl bg-nexus-paper p-3 text-[#0072FF] ring-1 ring-nexus-line">
                    <Icon size={22} />
                  </div>
                  <h3 className="font-display text-base font-bold text-nexus-ink">
                    {service.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-nexus-muted">
                    {service.description}
                  </p>
                </article>
              )
            })}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section
        id="contacto"
        className="relative scroll-mt-24 px-4 py-28 sm:px-6 lg:px-8 lg:py-36"
      >
        <div className="relative mx-auto max-w-4xl overflow-hidden rounded-[2rem] border border-nexus-line bg-white px-6 py-16 text-center shadow-soft sm:px-12 sm:py-20">
          <div
            aria-hidden
            className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(0,114,255,0.10),_transparent_55%),radial-gradient(ellipse_at_bottom_right,_rgba(15,159,138,0.08),_transparent_50%)]"
          />
          <div className="relative">
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-nexus-muted">
              Contacto directo
            </p>
            <h2 className="font-display mt-4 text-3xl font-extrabold tracking-tight text-nexus-ink sm:text-5xl">
              ¿Listo para escalar tu
              <br className="hidden sm:block" /> operación digital?
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-base text-nexus-muted">
              Escríbenos y te respondemos con una propuesta clara para tu
              proyecto.
            </p>

            <div className="mx-auto mt-8 grid max-w-xl gap-3 text-left sm:grid-cols-2">
              <a
                href={`mailto:${contactInfo.email}`}
                className="rounded-2xl border border-nexus-line bg-white/80 px-5 py-4 transition hover:border-[#0072FF]/35"
              >
                <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-nexus-muted">
                  <Mail size={12} />
                  Email
                </p>
                <p className="mt-1 break-all text-sm font-semibold text-nexus-ink">
                  {contactInfo.email}
                </p>
              </a>

              <div className="rounded-2xl border border-nexus-line bg-white/80 px-5 py-4">
                <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-nexus-muted">
                  <Phone size={12} />
                  Teléfono
                </p>
                <div className="mt-1 space-y-1">
                  {contactInfo.phones.map((phone) => (
                    <a
                      key={phone.href}
                      href={phone.href}
                      className="block text-sm font-semibold text-nexus-ink transition hover:text-[#0072FF]"
                    >
                      {phone.label}
                    </a>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-nexus-line bg-white/80 px-5 py-4 sm:col-span-2">
                <p className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-nexus-muted">
                  <MapPin size={12} />
                  Ubicación
                </p>
                <p className="mt-1 text-sm font-semibold text-nexus-ink">
                  {contactInfo.location}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Site footer */}
      <footer className="border-t border-nexus-line bg-white px-4 py-12 sm:px-6 lg:px-8 lg:py-14">
        <div className="mx-auto grid max-w-6xl gap-10 sm:grid-cols-2 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <img
                src="/images/isologo-nexus.png?v=2"
                alt=""
                className="h-10 w-10 rounded-full object-cover ring-1 ring-nexus-line"
              />
              <BrandMark />
            </div>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-nexus-muted">
              Software factory para construir, conectar e innovar operaciones
              digitales con ingeniería confiable.
            </p>
            <div className="mt-5 flex items-center gap-3">
              <a
                href={contactInfo.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-nexus-line text-nexus-muted transition hover:border-[#0072FF]/40 hover:text-[#0072FF]"
                aria-label="LinkedIn"
              >
                <Linkedin size={16} />
              </a>
              <a
                href={contactInfo.whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-10 items-center rounded-full border border-nexus-line px-4 text-sm font-medium text-nexus-ink transition hover:border-[#0072FF]/40 hover:text-[#0072FF]"
              >
                WhatsApp
              </a>
            </div>
          </div>

          <div className="lg:col-span-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-nexus-muted">
              Navegación
            </p>
            <ul className="mt-4 space-y-3">
              {navLinks.map((link) => (
                <li key={link.id}>
                  <button
                    type="button"
                    onClick={() => scrollToId(link.id)}
                    className="text-sm font-medium text-nexus-ink transition hover:text-[#0072FF]"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-nexus-muted">
              Contacto
            </p>
            <div className="mt-4 space-y-4">
              <p className="text-sm text-nexus-muted">
                {contactInfo.location}
              </p>
              <a
                href={`mailto:${contactInfo.email}`}
                className="flex items-start gap-2.5 text-sm text-nexus-ink transition hover:text-[#0072FF]"
              >
                <Mail size={15} className="mt-0.5 shrink-0 text-[#0072FF]" />
                <span className="break-all">{contactInfo.email}</span>
              </a>
              {contactInfo.phones.map((phone) => (
                <a
                  key={phone.href}
                  href={phone.href}
                  className="flex items-center gap-2.5 text-sm text-nexus-ink transition hover:text-[#0072FF]"
                >
                  <Phone size={15} className="shrink-0 text-[#0072FF]" />
                  {phone.label}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mx-auto mt-12 flex max-w-6xl flex-col gap-3 border-t border-nexus-line pt-6 text-xs text-nexus-muted sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {new Date().getFullYear()} NexusDevTech. Todos los derechos
            reservados.
          </p>
          <p className="tracking-wide">Web · Mobile · SaaS · Cloud · APIs</p>
        </div>
      </footer>
    </div>
  )
}
